/* eslint-disable no-restricted-imports */
/* eslint-disable complexity */
import {
	CircularProgress,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	styled,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { Linkable } from '@/components/blocks/Linkable';
import { useNotifications } from '@/data/Content/Notifications';
import { ChangeEvent, useEffect, useState } from 'react';
import { validateEmail, validateName, validatePhone } from 'integration/utils/regexForm';
import { addressFormFieldsSX } from '../../AddressForm/styles/fields';
import { AccountSelectSX } from '@/components/content/Account/styles/accountPersonal';
import { DpOutlinedInput } from '@/components/blocks/OutlinedInput';
import { BpCheckbox } from '@/components/content/Account/parts/InformationForm';
import ErrorIcon from '@mui/icons-material/Error';
import { DpOutlinedSelect } from '@/components/blocks/DpOutlineSelect';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import {
	DPCARD_FORM_URL,
	DPCARD_FORM_CLIENT_ID,
	DPCARD_FORM_DE_EXTERNAL_KEY,
	DPCARD_FORM_ACTION,
	DPCARD_FORM_RETURN_XML,
	DPCARD_FORM_SUCCESS_URL,
	DPCARD_FORM_ERROR_URL,
} from '@/data/config/DPCARD_FORM';
import { dpExecuteFormAction } from '@/components/blocks/DpLibrary/DpForm';
import useZipCode from '@/data/hooks/useZipCode';

interface FormDataInter {
	nombre: string;
	apellidos: string;
	email: string;
	telefono: string;
	zipCode: string;
	neighborhood: string;
	aceptarTerminos: boolean;
}

interface AddressInfoInter {
	city: string;
	state: string;
	neighborhood: string[];
}

const initialFormData: FormDataInter = {
	nombre: '',
	apellidos: '',
	email: '',
	telefono: '',
	zipCode: '',
	neighborhood: '',
	aceptarTerminos: false,
};

const initialAddressInfo: AddressInfoInter = {
	city: '',
	state: '',
	neighborhood: [],
};

export const DpCardForm = () => {
	const [formData, setFormData] = useState<FormDataInter>(initialFormData);
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const [errors, setErrors] = useState<{ [key: string]: string }>({});
	const { showSnackbarMessage } = useNotifications();
	const [addressInfo, setAddressInfo] = useState(initialAddressInfo);

	const handleZipCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
		const zipCode = event.target.value || '';

		if (/^\d*$/.test(zipCode) && zipCode.length <= 5) {
			handleChange(event);

			if (!zipCode || zipCode.length < 5) {
				setFormData((prevData) => ({
					...prevData,
					neighborhood: '', // Resetear el valor de colonia
				}));

				setAddressInfo({
					city: '',
					state: '',
					neighborhood: [],
				});
				return;
			}
		}
	};

	const HelperText = styled(Typography)<{
		showError: boolean;
	}>(({ theme, showError }) => ({
		color: showError ? theme.palette.error.main : theme.palette.grey[400],
		fontSize: 'var(--font-size-caption)',
		mt: 0.2,
		marginTop: '8px',
	}));

	const { data: solemtiAddress, isLoading: isValidatingZipCode } = useZipCode(formData.zipCode);

	useEffect(() => {
		if (solemtiAddress?.length > 0) {
			const solemtiAddressData = solemtiAddress[0];
			setAddressInfo({
				city: solemtiAddressData.Ciudad,
				state: solemtiAddressData.Estado,
				neighborhood: solemtiAddress.map((item) => item.Asentamiento),
			});
		}
	}, [solemtiAddress]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, type, checked } = e.target;
		if (name === 'telefono') {
			if (!validatePhone(value)) return;
		}
		if (name === 'nombre') {
			if (!validateName(value)) return;
		}
		if (name === 'apellidos') {
			if (!validateName(value)) return;
		}
		setErrors((prev) => ({
			...prev,
			[name]: '',
		}));
		setFormData({
			...formData,
			[name]: type === 'checkbox' ? checked : value,
		});
	};

	const handleSelectChange = (event: any) => {
		const { value, name } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));

		if (name === 'neighborhood') {
			setErrors((prev) => ({
				...prev,
				[name]: '',
			}));
		}
	};

	const validateForm = () => {
		const newErrors: { [key: string]: string } = {};

		if (!formData.nombre) newErrors.nombre = 'Este campo es requerido';

		if (!formData.apellidos) newErrors.apellidos = 'Este campo es requerido';

		if (formData.telefono.length < 10) newErrors.telefono = 'Este campo es requerido';

		if (!validateEmail(formData.email)) newErrors.email = 'Correo electrónico inválido.';

		if (!validatePhone(formData.telefono)) {
			newErrors.telefono = 'Teléfono inválido, solo se permiten hasta 10 dígitos.';
		}
		if (!formData.telefono) newErrors.telefono = 'Este campo es requerido.';

		if (!formData.aceptarTerminos) {
			newErrors.aceptarTerminos = 'Debes aceptar el aviso de privacidad.';
		}
		if (!formData.zipCode) {
			newErrors.zipCode = 'Este campo es requerido';
		} else if (formData.zipCode.length !== 5 || !/^\d{5}$/.test(formData.zipCode)) {
			newErrors.zipCode = 'Este código postal es inválido.';
		}
		if (!formData.neighborhood) newErrors.neighborhbood = 'Este campo es requerido.';
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};
	const handleBlur = (name: string) => {
		if (name === 'email') {
			const emailValue = formData[name] || '';
			if (validateEmail(emailValue)) {
				setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
			} else {
				setErrors((prevErrors) => ({ ...prevErrors, [name]: 'Correo electrónico no válido' }));
			}
		} else if (name === 'telefono') {
			if (formData[name].length < 10) {
				setErrors((prevErrors) => ({
					...prevErrors,
					[name]: 'Solo se permiten hasta 10 dígitos.',
				}));
			}
		} else {
			if (!formData[name as keyof FormDataInter]) {
				setErrors((prevErrors) => ({ ...prevErrors, [name]: 'Este campo es requerido' }));
			}
		}
	};

	const onMessageError = (error?: string) => {
		showSnackbarMessage(
			error || 'Ocurrió un error al enviar el formulario. Por favor, inténtelo de nuevo.'
		);
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			return;
		}

		try {
			await dpExecuteFormAction(
				{
					_clientID: DPCARD_FORM_CLIENT_ID,
					_deExternalKey: DPCARD_FORM_DE_EXTERNAL_KEY,
					_action: DPCARD_FORM_ACTION,
					_returnXML: DPCARD_FORM_RETURN_XML,
					_successURL: DPCARD_FORM_SUCCESS_URL,
					_errorURL: DPCARD_FORM_ERROR_URL,
					Ciudad: addressInfo.city,
					Nombres: formData['nombre'],
					Apellidos: formData['apellidos'],
					Correo: formData['email'],
					Celular: formData['telefono'],
					Colonia: `${formData['neighborhood']}, ${formData.zipCode}, ${addressInfo.city}`,
				},
				DPCARD_FORM_URL,
				'post'
			);
		} catch (error) {
			onMessageError();
		} finally {
			setFormData(initialFormData);
			setAddressInfo(initialAddressInfo);
		}
	};

	return (
		<Grid
			container
			spacing={2}
			// sx={{
			// 	margin: '0px 20px 20px 20px',
			// 	'@media(min-width:960px)': { margin: '0px 0px 20px 0px' },
			// }}
		>
			<Grid item xs={12}>
				<Typography
					component={'h1'}
					sx={{
						fontSize: 'var(--font-size-xl)',
						fontWeight: 800,
						textAlign: { xs: 'center', md: 'left' },
					}}
				>
					{'DPCARD JALA CONTIGO, SIN COMISIÓN POR APERTURA DE CRÉDITO'}
				</Typography>
				<Typography
					sx={{ display: { xs: 'none', md: 'block' }, fontSize: 'var(--font-size-body)' }}
				>
					{'Sin comisión por manejo de cuenta y promociones exclusivas'}
				</Typography>
			</Grid>
			<Grid container item xs={12} spacing={2}>
				<Grid item xs={12} md={6}>
					<DpOutlinedInput
						required
						name="nombre"
						type="text"
						label="Nombre"
						value={formData.nombre}
						onChange={handleChange}
						onBlur={(e) => handleBlur(e.target.name)}
						error={!!errors.nombre}
						helperText={errors.nombre}
						fullWidth
						sx={addressFormFieldsSX}
						autoComplete="email"
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<DpOutlinedInput
						label="Apellidos"
						fullWidth
						name="apellidos"
						value={formData.apellidos}
						onChange={handleChange}
						onBlur={(e) => handleBlur(e.target.name)}
						error={!!errors.apellidos}
						helperText={errors.apellidos}
						sx={addressFormFieldsSX}
					/>
				</Grid>
			</Grid>
			<Grid container item xs={12} gap={1}>
				<Grid container item xs={12} spacing={2}>
					<Grid item xs={12} md={6}>
						<DpOutlinedInput
							label="Correo electrónico"
							fullWidth
							name="email"
							value={formData.email}
							onChange={handleChange}
							onBlur={(e) => handleBlur(e.target.name)}
							error={!!errors.email}
							helperText={errors.email}
							FormHelperTextProps={{
								style: { padding: 0, margin: 0 },
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment sx={{ zIndex: 99 }} position="end">
										<Tooltip
											title="ⓘ En el correo solo usar letras minúsculas, sin espacios."
											arrow
											open={tooltipOpen}
											onClose={() => setTooltipOpen(false)}
											onOpen={() => setTooltipOpen(true)}
											disableInteractive
										>
											<IconButton
												onClick={() => setTooltipOpen((prev) => !prev)}
												sx={{ width: '10px', height: '10px', color: 'rgb(201, 201, 201)' }}
											>
												<ErrorIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</InputAdornment>
								),
							}}
							sx={addressFormFieldsSX}
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<DpOutlinedInput
							label="Teléfono"
							fullWidth
							name="telefono"
							value={formData.telefono}
							onChange={handleChange}
							onBlur={(e) => handleBlur(e.target.name)}
							error={!!errors.telefono}
							helperText={errors.telefono}
							FormHelperTextProps={{
								sx: {
									padding: 0,
									margin: 0,
								},
							}}
							sx={addressFormFieldsSX}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} sm={6}>
				<DpOutlinedInput
					required
					id="address-form-zipCode"
					name="zipCode"
					data-testid="address-form-zipCode"
					label={'Código postal'}
					onChange={handleZipCodeChange}
					onBlur={(e) => handleBlur(e.target.name)}
					value={formData.zipCode}
					inputProps={{ maxLength: 5, inputMode: 'numeric', pattern: '[0-9]*' }}
					fullWidth
					autoComplete="postal-code"
					error={!!errors.zipCode}
					helperText={errors.zipCode}
					FormHelperTextProps={{
						style: { padding: 0, margin: 0 },
					}}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								{isValidatingZipCode ? <CircularProgress size={20} /> : null}
							</InputAdornment>
						),
					}}
					sx={addressFormFieldsSX}
				/>
			</Grid>
			<Grid item xs={12} sm={6}>
				<FormControl
					fullWidth
					required
					variant="outlined"
					sx={{ borderRadius: '5.5px' }}
					disabled={!addressInfo.neighborhood || addressInfo.neighborhood.length === 0}
				>
					<InputLabel
						sx={{
							backgroundColor: '#FFF',
							padding: '0px',
							color: 'inherit',
							lineHeight: 1,
							height: '20px',
							fontSize: 'var(--font-size-body)',
						}}
						id="neighborhood-label"
					>
						{'Colonia'}
					</InputLabel>
					<DpOutlinedSelect
						name="neighborhood"
						fullWidth
						value={formData.neighborhood || ''}
						onChange={handleSelectChange}
						onBlur={(e) => handleBlur(e.target.name)}
						labelId="neighborhood-label"
						disabled={!addressInfo.neighborhood || addressInfo.neighborhood.length === 0}
						sx={{
							...AccountSelectSX,
							'& .MuiOutlinedInput-notchedOutline': {
								borderColor: errors.neighborhood ? '#CE1616' : '#b5beca',
							},
							'&:hover .MuiOutlinedInput-notchedOutline': {
								borderColor: errors.neighborhood ? '#CE1616' : '#b5beca',
							},
							'& .MuiSelect-select': {
								color: 'inherit',
							},
						}}
						error={!!errors.neighborhood}
					>
						{addressInfo.neighborhood
							? addressInfo.neighborhood.map((nh) => (
									<MenuItem key={nh} value={nh}>
										{nh}
									</MenuItem>
							  ))
							: null}
					</DpOutlinedSelect>
					{errors.neighborhood ? (
						<HelperText sx={{ marginTop: '0px' }} showError={true}>
							{errors.neighborhood}
						</HelperText>
					) : null}
				</FormControl>
			</Grid>

			<Grid item xs={12} sm={6}>
				<TextField
					required
					data-testid="state-textfield"
					id="state-textfield"
					name="state"
					label={'Estado o provincia'}
					value={addressInfo.state}
					inputProps={{ maxLength: 40 }}
					fullWidth
					disabled
					InputLabelProps={{
						style: {
							height: '48px',
							lineHeight: 1,
							padding: '0px',
							fontSize: 'var(--font-size-body)',
						},
					}}
					InputProps={{
						style: { height: '48px' },
					}}
				/>
			</Grid>
			<Grid item xs={12} sm={6}>
				<TextField
					required
					id="address-form-city"
					data-testid="address-form-city"
					name="city"
					// error={error.city}
					label={'Ciudad o municipio'}
					value={addressInfo.city}
					inputProps={{ maxLength: 40 }}
					fullWidth
					disabled
					InputLabelProps={{
						style: {
							height: '48px',
							lineHeight: 1,
							padding: '0px',
							fontSize: 'var(--font-size-body)',
						},
					}}
					InputProps={{
						style: { height: '48px' },
					}}
					// helperText={helperText.city}
				/>
			</Grid>

			<Grid
				item
				xs={12}
				sx={{ pb: { xs: '12px', sm: '0px' }, marginBottom: '10px', mt: 'var(--spacing-1)' }}
			>
				<FormControlLabel
					control={
						<BpCheckbox
							checked={formData.aceptarTerminos}
							name="aceptarTerminos"
							onChange={handleChange}
						/>
					}
					label={
						<Typography sx={{ fontSize: 'var(--font-size-default)' }}>
							{'He leído y consentido el '}
							<Linkable
								href="https://dpcard.mx/aviso-privacidad"
								target="_blank"
								sx={{ color: '#0467D2', fontSize: 'var(--font-size-default)' }}
							>
								{'Aviso de privacidad'}
							</Linkable>
						</Typography>
					}
				/>
				{errors.aceptarTerminos ? (
					<Typography
						sx={{
							color: '#d32f2f',
							fontSize: 'var(--font-size-caption)',
							marginBottom: '8px',
						}}
					>
						{errors.aceptarTerminos}
					</Typography>
				) : null}
			</Grid>
			<Grid sx={{ pl: '16px', width: '100%' }}>
				<DpButton fullWidth onClick={handleSubmit}>
					{'Solicitar información'}
				</DpButton>
			</Grid>
		</Grid>
	);
};
